import type { SurveyLanguage } from '../types/survey';
import type { ApiResponse } from '../internalTypes';

const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  en: 'English',
  hi: 'Hindi',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  bn: 'Bengali',
  gu: 'Gujarati',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  mr: 'Marathi',
  pa: 'Punjabi',
  ta: 'Tamil',
  te: 'Telugu',
  ur: 'Urdu',
  zh: '中文',
};

/**
 * Resolves the full human-readable display name for a given language code.
 *
 * @param code The ISO language code (e.g. 'de', 'en').
 * @returns The resolved display name string.
 */
export function getLanguageName(code: string): string {
  const norm = code.toLowerCase().trim();
  return LANGUAGE_DISPLAY_NAMES[norm] || code.toUpperCase();
}

/**
 * Traverses all questions and translations to gather all unique languages supported by the survey.
 *
 * @param api The raw API response envelope containing the DTO pages.
 * @returns List of supported language objects with code and resolved names.
 */
export function extractSurveyLanguages(api: ApiResponse): SurveyLanguage[] {
  const dto = api.cfmSurveyDTO;
  const languagesSet = new Set<string>();
  const defaultLanguage = dto?.baseLanguage ?? 'en';
  languagesSet.add(defaultLanguage);

  if (dto && Array.isArray(dto.surveyPages)) {
    dto.surveyPages.forEach(page => {
      (page.questionHolders ?? []).forEach(holder => {
        const q = holder.question;
        if (q && q.translations) {
          Object.keys(q.translations).forEach(lang => {
            languagesSet.add(lang);
          });
        }
      });
    });
  }

  return Array.from(languagesSet).map(lang => ({
    code: lang,
    name: getLanguageName(lang),
  }));
}
